/**
 * DEV-1164 iter 2 — Side tray for a single Recurring Duty task.
 *
 * Slides in from the right edge of the viewport when the user clicks a
 * task row in the RhythmDisplayPanel. Shows:
 *   - Short name (header)
 *   - Cadence + cadence anchor summary
 *   - Lead time + due window
 *   - Full description (the rich one, from role_rhythm_tasks.description)
 *   - Step placeholder (steps render once DEV-1162 Phase 2e ships)
 *
 * Dismiss: backdrop click, Escape key, or close button.
 *
 * Per the 2026-05-31 mockup design lock, this replaces the modal/expand
 * pattern with a side tray. Tray is fixed-position (full-viewport
 * overlay) with a translucent backdrop + sliding card.
 */

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import type { RhythmTaskDetail } from "../../api/roleRhythms";
import { getCurrentUserGuid } from "../../api/client";
import { mediaSrc } from "../../api/mediaUrl";
import RhythmStepList from "../../Components/RhythmStepList/RhythmStepList";
import { cadenceLabel, cadenceAnchorSummary } from "./cadenceFormatter";
import styles from "./RhythmTaskTray.module.css";

/**
 * DEV-1256. Open the ChatTray (bottom-right floating panel) with a DM
 * to the given user. App-level listener in App.tsx catches the event,
 * sets chatTrayOpen=true, and forwards the user GUID to ChatTray which
 * dedupes or creates the conversation. Keeps the user in their current
 * page (Engagements / Action Center) instead of routing to /messages.
 */
function openChatTrayWith(userGuid: string) {
  window.dispatchEvent(
    new CustomEvent("l4e:open-chat-with", { detail: { userGuid } }),
  );
}

interface RhythmTaskTrayProps {
  task: RhythmTaskDetail;
  onClose: () => void;
}

// DEV-1251 P3. Cadence formatters moved to ./cadenceFormatter so the
// Action Center ActionItemTray can reuse them. Imported above.

export function RhythmTaskTray({ task, onClose }: RhythmTaskTrayProps) {
  const { t } = useAppTranslation("pages");
  const closeRef = useRef<HTMLButtonElement>(null);

  // Escape key dismiss + focus the close button on open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    closeRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isStanding = task.taskKind === "standing_duty";
  const cadenceText = cadenceLabel(task.cadenceType, t);
  const anchorText = cadenceAnchorSummary(task.cadenceType, task.cadenceAnchor);

  // DEV-1257-followup. Portal to document.body. EngagementsPage has CSS
  // `filter: brightness(...)` rules on several ancestors, and per the CSS
  // spec a filter creates a containing block for position:fixed descendants.
  // Without the portal, the backdrop's `position: fixed` was being trapped
  // inside one of those filter-containing ancestors instead of escaping to
  // the viewport — placing the tray off-screen to the right (button right
  // edge measured at 2214px on a normal viewport). Rendering directly into
  // document.body sidesteps every Engagements ancestor and restores the
  // intended viewport-fixed behavior.
  return createPortal(
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={t("engagements.rhythmTrayAria")}
    >
      <aside className={styles.tray}>
        <header className={styles.trayHeader}>
          <div className={styles.trayEyebrow}>
            {isStanding
              ? t("engagements.rhythmStandingDuty")
              : t("engagements.rhythmRecurringDuty")}
          </div>
          <button
            ref={closeRef}
            type="button"
            className={styles.trayClose}
            onClick={onClose}
            aria-label={t("engagements.rhythmTrayClose")}
          >
            ×
          </button>
        </header>

        <h2 className={styles.trayTitle}>{task.name}</h2>

        <div className={styles.trayMetaGrid}>
          {!isStanding && cadenceText && (
            <>
              <div className={styles.trayMetaLabel}>
                {t("engagements.rhythmTrayCadence")}
              </div>
              <div className={styles.trayMetaValue}>
                {cadenceText}
                {anchorText && (
                  <span className={styles.trayMetaSub}>{anchorText}</span>
                )}
              </div>
              <div className={styles.trayMetaLabel}>
                {t("engagements.rhythmTrayLeadTime")}
              </div>
              <div className={styles.trayMetaValue}>
                {t("engagements.rhythmTrayLeadTimeValue").replace(
                  "{{n}}",
                  String(task.leadTimeDays),
                )}
              </div>
              <div className={styles.trayMetaLabel}>
                {t("engagements.rhythmTrayDueWindow")}
              </div>
              <div className={styles.trayMetaValue}>
                {t("engagements.rhythmTrayDueWindowValue").replace(
                  "{{n}}",
                  String(task.dueWindowDays),
                )}
              </div>
            </>
          )}
          {isStanding && (
            <>
              <div className={styles.trayMetaLabel}>
                {t("engagements.rhythmTrayCadence")}
              </div>
              <div className={styles.trayMetaValue}>
                {t("engagements.rhythmStandingValue")}
              </div>
            </>
          )}
        </div>

        {task.description && (
          <>
            <div className={styles.trayMetaLabel}>
              {t("engagements.rhythmTrayDescription")}
            </div>
            <p className={styles.trayDescription}>{task.description}</p>
          </>
        )}

        {/*
          DEV-1240 P3 — Contact person row. Renders only when the task has
          a resolved user contact. The Message button navigates to the
          Messages page with a startChatWith query param; MessagesPage
          uses that to open an existing DM or create a new one with
          this user. Self-contact suppresses the Message button (you
          can't DM yourself) but still shows the row so the admin's
          intent is visible.
        */}
        {task.contactUser && (() => {
          const me = getCurrentUserGuid().toUpperCase();
          const them = task.contactUser.userGuid.toUpperCase();
          const isSelf = me === them;
          return (
            <>
              <div className={styles.trayMetaLabel}>
                {t("engagements.rhythmTrayContact")}
              </div>
              <div className={styles.trayContactRow}>
                {task.contactUser.avatarUrl ? (
                  <img
                    src={mediaSrc(task.contactUser.avatarUrl) ?? undefined}
                    alt=""
                    className={styles.trayContactAvatar}
                  />
                ) : (
                  <span className={styles.trayContactAvatarFallback} aria-hidden="true">
                    {task.contactUser.name.trim().charAt(0).toUpperCase() || "?"}
                  </span>
                )}
                <span className={styles.trayContactName}>{task.contactUser.name}</span>
                {!isSelf && (
                  <button
                    type="button"
                    className={styles.trayContactMessageBtn}
                    onClick={() => openChatTrayWith(task.contactUser!.userGuid)}
                  >
                    💬 {t("engagements.rhythmTrayMessage")}
                  </button>
                )}
              </div>
            </>
          );
        })()}

        {/*
          DEV-1162 Phase 2e — Step rendering placeholder. Steps array is
          currently always empty (table doesn't exist yet); when 2e ships
          this section automatically populates.
        */}
        {task.steps.length > 0 && (
          <>
            <div className={styles.trayMetaLabel}>
              {t("engagements.rhythmTraySteps")}
            </div>
            {/* DEV-1251 P2. Step rendering extracted to shared component
                so the Action Center tray (DEV-1254) can reuse the exact
                same row shape. Adds the Required badge for free vs the
                pre-extraction inline version. */}
            <RhythmStepList steps={task.steps} />
          </>
        )}
      </aside>
    </div>,
    document.body,
  );
}

export default RhythmTaskTray;
